/* eslint-disable react/no-array-index-key */
import React, { lazy, Suspense, Fragment } from "react";
import { Switch, Redirect, Route } from "react-router-dom";
import DashboardLayout from "src/layouts/DashboardLayout";
import DocsLayout from "src/layouts/DocsLayout";
import MainLayout from "src/layouts/MainLayout";
import HomeView from "src/views/pages/HomeView";
import LoadingScreen from "src/components/LoadingScreen";
import AuthGuard from "src/components/Guard/AuthGuard";
import GuestGuard from "src/components/Guard/GuestGuard";
import Guide from "./views/pages/HomeView/Guide";
import AdminGuard from "./components/Guard/AdminGuard";
import ReportAnalystAssessorGuard from "./components/Guard/ReportAnalyst/AssessorGuard";
import ReportAnalystTreasurerGuard from "./components/Guard/ReportAnalyst/TreasurerGuard";
import ProvencialTreasurerGuard from "./components/Guard/ReportAnalyst/ProvencialTreasurerGuard";
import ProvencialAssessorGuard from "./components/Guard/ReportAnalyst/ProvencialAssessorGuard";

const routesConfig = [
  {
    exact: true,
    path: "/",
    component: () => <Redirect to="/home" />,
  },
  {
    exact: true,
    path: "/404",
    component: lazy(() => import("src/views/pages/Error404View")),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: "/login",
    component: lazy(() => import("src/views/auth/LoginView")),
  },
  {
    exact: true,
    path: "/login-unprotected",
    component: lazy(() => import("src/views/auth/LoginView")),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: "/register",
    component: lazy(() => import("src/views/auth/RegisterView")),
  },
  {
    exact: true,
    path: "/register-unprotected",
    component: lazy(() => import("src/views/auth/RegisterView")),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: "/recover/password",
    component: lazy(() => import("src/views/auth/RecoverPasswordView")),
  },

  {
    exact: true,
    guard: GuestGuard,
    path: "/recover/password/reset-form/:email/:token",
    component: lazy(() =>
      import("src/views/auth/RecoverPasswordView/NewPasswordView/")
    ),
  },

  // user
  {
    path: "/app",
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: "/app",
        component: () => <Redirect to="/app/reports/dashboard" />,
      },
      {
        exact: true,
        path: "/app/property/register",
        component: lazy(() =>
          import("src/views/reports/DashboardView/Property/index")
        ),
      },
      {
        exact: true,
        path: "/app/account",
        component: lazy(() => import("src/views/pages/AccountView")),
      },
      {
        exact: true,
        path: "/app/reports/dashboard",
        component: lazy(() => import("src/views/reports/DashboardView")),
      },
      {
        exact: true,
        path: "/app/reports/dashboard/property/holdings",
        component: lazy(() =>
          import("src/views/reports/DashboardView/Property/holdings")
        ),
      },
      {
        exact: true,
        path: "/app/reports/dashboard/property/history",
        component: lazy(() =>
          import("src/views/reports/DashboardView/Property/history")
        ),
      },
      {
        exact: true,
        path: "/app/reports/dashboard/property/soa",
        component: lazy(() =>
          import("src/views/reports/DashboardView/Property/soa")
        ),
      },
      {
        exact: true,
        path: "/app/paynamics/payment/successfull/:userid/:amount",
        component: lazy(() =>
          import(
            "src/views/reports/DashboardView/PaymentPaynamics/PaynamicsPaymentSuccess"
          )
        ),
      },
      {
        exact: true,
        path: "/app/reports/dashboard-alternative",
        component: lazy(() =>
          import("src/views/reports/DashboardAlternativeView")
        ),
      },
      {
        exact: true,
        path: "/app/reports",
        component: () => <Redirect to="/app/reports/dashboard" />,
      },

      {
        exact: true,
        path: "/app/calendar",
        component: lazy(() => import("src/views/calendar/CalendarView")),
      },

      {
        exact: true,
        path: "/app/extra/charts/apex",
        component: lazy(() => import("src/views/extra/charts/ApexChartsView")),
      },

      {
        exact: true,
        path: "/app/business/registration",
        component: lazy(() =>
          import(
            "src/views/reports/DashboardView/EnrollmentRequest/EnrollBusToUser"
          )
        ),
      },

      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },

  // administrator
  {
    path: "/administrator",
    layout: DashboardLayout,
    guard: AdminGuard,
    routes: [
      {
        exact: true,
        path: "/administrator",
        component: () => <Redirect to="/administrator/dashboard" />,
      },
      {
        exact: true,
        path: "/administrator/users",
        component: lazy(() => import("src/views/reports/DashboardAdmin/Users")),
      },
      {
        exact: true,
        path: "/administrator/dashboard",
        component: lazy(() => import("src/views/reports/DashboardAdmin")),
      },
      {
        exact: true,
        path: "/administrator/email/sent",
        component: lazy(() => import("src/views/reports/DashboardAdmin/Email")),
      },
      {
        exact: true,
        path: "/administrator/account",
        component: lazy(() => import("src/views/pages/AccountView")),
      },
      {
        exact: true,
        path: "/administrator/reports/payments",
        component: lazy(() =>
          import("src/views/reports/DashboardAdmin/paynamics")
        ),
      },

      {
        exact: true,
        path: "/administrator/reports/property-enrollment",
        component: lazy(() =>
          import("src/views/reports/DashboardAdmin/PropertyEnrollment")
        ),
      },

      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },

  // report analyst assessor
  {
    path: "/report-analyst-assessor",
    layout: DashboardLayout,
    guard: ReportAnalystAssessorGuard,
    routes: [
      {
        exact: true,
        path: "/report-analyst-assessor",
        component: () => <Redirect to="/report-analyst-assessor/dashboard" />,
      },
      {
        exact: true,
        path: "/report-analyst-assessor/dashboard",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/assessor")
        ),
      },

      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },

  // report analyst treasurer
  {
    path: "/report-analyst-treasurer",
    layout: DashboardLayout,
    guard: ReportAnalystTreasurerGuard,
    routes: [
      {
        exact: true,
        path: "/report-analyst-treasurer",
        component: () => <Redirect to="/report-analyst-treasurer/dashboard" />,
      },
      {
        exact: true,
        path: "/report-analyst-treasurer/dashboard",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer")
        ),
      },

      {
        exact: true,
        path: "/report-analyst-treasurer/delinquent",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer/graph-delinquent")
        ),
      },

      {
        exact: true,
        path: "/report-analyst-treasurer/business",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer/bpls_graph/index")
        ),
      },

      {
        exact: true,
        path: "/report-analyst-treasurer/general-collection",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer/general-collection")
        ),
      },

      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },

  // provencial report analyst assessor
  {
    path: "/provencial-report-analyst-assessor",
    layout: DashboardLayout,
    guard: ProvencialAssessorGuard,
    routes: [
      {
        exact: true,
        path: "/provencial-report-analyst-assessor",
        component: () => (
          <Redirect to="/provencial-report-analyst-assessor/dashboard" />
        ),
      },
      {
        exact: true,
        path: "/provencial-report-analyst-assessor/dashboard",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/assessor")
        ),
      },

      {
        exact: true,
        path: "/provencial-report-analyst-assessor/rpus-municipality",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/assessor/municipality-rpus")
        ),
      },

      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },

  // provencial report analyst treasurer
  {
    path: "/provencial-report-analyst-treasurer",
    layout: DashboardLayout,
    guard: ProvencialTreasurerGuard,
    routes: [
      {
        exact: true,
        path: "/provencial-report-analyst-treasurer",
        component: () => (
          <Redirect to="/provencial-report-analyst-treasurer/dashboard" />
        ),
      },
      {
        exact: true,
        path: "/provencial-report-analyst-treasurer/dashboard",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer")
        ),
      },

      {
        exact: true,
        path: "/provencial-report-analyst-treasurer/delinquent",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer/graph-delinquent")
        ),
      },

      {
        exact: true,
        path: "/provencial-report-analyst-treasurer/business",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer/bpls_graph")
        ),
      },

      {
        exact: true,
        path: "/provencial-report-analyst-treasurer/general-collection",
        component: lazy(() =>
          import("src/views/reports/ReportAnalyst/treasurer/general-collection")
        ),
      },

      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },

  // docs
  {
    path: "/docs",
    layout: DocsLayout,
    routes: [
      {
        exact: true,
        path: "/docs",
        component: () => <Redirect to="/docs/welcome" />,
      },
      {
        exact: true,
        path: "/docs/welcome",
        component: lazy(() => import("src/views/docs/WelcomeView")),
      },
      {
        exact: true,
        path: "/docs/getting-started",
        component: lazy(() => import("src/views/docs/GettingStartedView")),
      },
      {
        exact: true,
        path: "/docs/environment-variables",
        component: lazy(() =>
          import("src/views/docs/EnvironmentVariablesView")
        ),
      },
      {
        exact: true,
        path: "/docs/deployment",
        component: lazy(() => import("src/views/docs/DeploymentView")),
      },
      {
        exact: true,
        path: "/docs/api-calls",
        component: lazy(() => import("src/views/docs/ApiCallsView")),
      },
      {
        exact: true,
        path: "/docs/analytics",
        component: lazy(() => import("src/views/docs/AnalyticsView")),
      },
      {
        exact: true,
        path: "/docs/authentication",
        component: lazy(() => import("src/views/docs/AuthenticationView")),
      },
      {
        exact: true,
        path: "/docs/routing",
        component: lazy(() => import("src/views/docs/RoutingView")),
      },
      {
        exact: true,
        path: "/docs/settings",
        component: lazy(() => import("src/views/docs/SettingsView")),
      },
      {
        exact: true,
        path: "/docs/state-management",
        component: lazy(() => import("src/views/docs/StateManagementView")),
      },
      {
        exact: true,
        path: "/docs/theming",
        component: lazy(() => import("src/views/docs/ThemingView")),
      },
      {
        exact: true,
        path: "/docs/support",
        component: lazy(() => import("src/views/docs/SupportView")),
      },
      {
        exact: true,
        path: "/docs/changelog",
        component: lazy(() => import("src/views/docs/ChangelogView")),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },

  // all
  {
    path: "*",
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: "/home",
        component: HomeView,
      },
      {
        exact: true,
        path: "/home/guide",
        component: Guide,
      },
      {
        exact: true,
        path: "/pricing",
        component: lazy(() => import("src/views/pages/PricingView")),
      },
      {
        exact: true,
        path: "/home/terms-and-conditions",
        component: lazy(() => import("src/views/pages/TermsAndConditions")),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
];

const renderRoutes = (routes) =>
  routes ? (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, i) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const Component = route.component;

          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={(props) => (
                <Guard>
                  <Layout>
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  ) : null;

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
